import { format } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useContext, useEffect, useRef, useState } from 'react'
import { Breadcrumbs, IconButton, InputAdornment, TextField, Typography } from '@mui/material'

import { FormError } from '../types'
import { numberWithCommas } from '../utils'
import Chip from '../components/common/chip'
import { getBatchesData } from '../api/payments.api'
import { requestAccessToken } from '../api/auth.api'
import HashIcon from '../assets/icons/hash-icon.svg'
import SearchIcon from '../assets/icons/search-icon.svg'
import TicketIcon from '../assets/icons/ticket-icon.svg'
import Pagination from '../components/common/pagination'
import { textFieldWhiteBg } from '../theme/custom-styles'
import { handleIsValidMatch } from '../utils/common.utils'
import DownloadIcon from '../assets/icons/download-icon.svg'
import CalendarIcon from '../assets/icons/calendar-icon.svg'
import MoneyGreenIcon from '../assets/icons/money-icon-green.svg'
import CheckIconGreen from '../assets/icons/check-icon-green.svg'
import { AppStateContext, AuthContext, UserContext } from '../contexts'
import { PaymentsHistoryModal } from '../components/insights-details-management/payments-history-modal'

const PaymentMovementsManagement = () => {
  const navigate = useNavigate()
  const currentListPageRef = useRef<number>(1)
  const batchesDataRef = useRef<Array<any>>([])
  const { userSession } = useContext(UserContext)
  const componentDidMount = useRef<boolean>(false)
  const { t } = useTranslation(['reports-management'])
  const { isAuth, removeAuth } = useContext(AuthContext)
  const { setScrollToTop, updateLoadingStatus, handleStopTimer } = useContext(AppStateContext)

  const [errors, setErrors] = useState<FormError>({})
  const [pageSize, setPageSize] = useState<number>(50)
  const [batchesData, setBatchesData] = useState<Array<any>>([])
  const [batchesList, setBatchesList] = useState<Array<any>>([])
  const [batchToSearch, setBatchToSearch] = useState<string>('')
  const [currentListPage, setCurrentListPage] = useState<number>(1)
  const [searchedBatches, setSearchedBatches] = useState<Array<any>>([])
  const [openPaymentsHistoryModal, setOpenPaymentsHistoryModal] = useState<boolean>(false)

  useEffect(() => {
    if (!userSession) {
      return
    }

    if ((!userSession.subRole && userSession?.role !== 'super-admin') || (userSession.subRole && userSession.subRole !== 'reconciliation-specialist')) {
      navigate('/404')
      return
    }

    setTimeout(() => {
      updateLoadingStatus?.(true)
      handleGetComponentData()
    }, 1)

    return () => {
      handleStopTimer?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSession])

  useEffect(() => {
    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const batchesPerPage = batchToSearch === '' && searchedBatches.length === 0 ? batchesData.slice(firstPageIndex, lastPageIndex) : searchedBatches.slice(firstPageIndex, lastPageIndex)

    setBatchesList(batchesPerPage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentListPage, batchesData, searchedBatches])

  const handleGetComponentData = async () => {
    try {
      const {
        data: { accessToken }
      } = await requestAccessToken(String(isAuth).replaceAll('"', ''))

      handleGetBatchesData(accessToken)
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleGetBatchesData = async (accessToken: string) => {
    try {
      const {
        data: { currentBatchesData }
      } = await getBatchesData(accessToken)

      batchesDataRef.current = currentBatchesData
      setBatchesData(currentBatchesData)
      updateLoadingStatus?.(false)

      if (componentDidMount.current === false) {
        componentDidMount.current = true
      }
    } catch (error: any) {
      console.log(error, error?.response?.data?.message)
      if (error?.response?.status === 401 && error?.response?.data?.message === 'Not Authorized: Expired') {
        handleCloseSession()
      }
    }
  }

  const handleSetBatchesDataToSearch = (batchToSearchData: string, showError: boolean) => {
    currentListPageRef.current = 1

    setErrors({})
    setCurrentListPage(1)
    setBatchToSearch(batchToSearchData)

    if (batchToSearchData === '') {
      const firstPageIndex = (currentListPage - 1) * pageSize
      const lastPageIndex = firstPageIndex + pageSize
      const currentBatchesHistoryDataState = batchesDataRef.current ?? []
      const batchesHistoryPerPage = currentBatchesHistoryDataState.slice(firstPageIndex, lastPageIndex)

      setBatchesList(batchesHistoryPerPage)
      setSearchedBatches([])

      if (showError) setErrors({ batchToSearch: t('paymentMovements.searchField.errors.emptyField', { ns: 'reports-management' }) })
      return
    }

    const currentFullBatchesData = [...(batchesDataRef.current as Array<any>)]
    const filteredBatchesHistory = currentFullBatchesData.filter((batchData) => {
      const batchStatusText = handleGetBatchChipStatusText(batchData.status)
      const batchDateText = String(format(new Date(batchData.createdAt), 'dd/MM/yyyy | HH:mm:ss'))
      const batchPaymentReferenceText =
        Array.isArray(batchData.cobrePaymentReference) && batchData.cobrePaymentReference.length > 0 && batchData.cobrePaymentReference[0].paymentReference ? batchData.cobrePaymentReference[0].paymentReference : ''

      const batchDate = handleIsValidMatch(batchToSearchData.toLowerCase(), String(batchDateText).toLowerCase())
      const batchStatus = handleIsValidMatch(batchToSearchData.toLowerCase(), String(batchStatusText).toLowerCase())
      const totalAmount = handleIsValidMatch(batchToSearchData.toLowerCase(), String(batchData.totalAmount).toLowerCase())
      const paymentCount = handleIsValidMatch(batchToSearchData.toLowerCase(), String(batchData.paymentCount).toLowerCase())
      const batchOperationName = handleIsValidMatch(batchToSearchData.toLowerCase(), String(batchData.originalName).toLowerCase())
      const batchPaymentReference = handleIsValidMatch(batchToSearchData.toLowerCase(), String(batchPaymentReferenceText).toLowerCase())

      return totalAmount || paymentCount || batchStatus || batchDate || batchOperationName || batchPaymentReference
    })

    const firstPageIndex = (currentListPage - 1) * pageSize
    const lastPageIndex = firstPageIndex + pageSize
    const reportsHistoryPerPage = filteredBatchesHistory.slice(firstPageIndex, lastPageIndex)

    setBatchesList(reportsHistoryPerPage)
    setSearchedBatches(filteredBatchesHistory)
  }

  const handleGetBatchChipStatusText = (batchStatus: string) => {
    if (batchStatus === 'uploaded' || batchStatus === 'analyzing' || batchStatus === 'downloaded' || batchStatus === 'downloaded-n-analyzing') {
      return t('listItem.chipStatusTexts.analyzing', { ns: 'documents-to-review-management' })
    }

    if (batchStatus === 'error-downloading-file') {
      return t('listItem.chipStatusTexts.downloadError', { ns: 'documents-to-review-management' })
    }

    if (batchStatus === 'error-analyzing-file') {
      return t('listItem.chipStatusTexts.fileError', { ns: 'documents-to-review-management' })
    }

    if (batchStatus === 'stopped') {
      return t('listItem.chipStatusTexts.stoppedAnalysis', { ns: 'documents-to-review-management' })
    }

    if (batchStatus === 'error') {
      return t('listItem.chipStatusTexts.cobreError', { ns: 'documents-to-review-management' })
    }

    if (batchStatus === 'analysis-completed') {
      return t('listItem.chipStatusTexts.completedAnalysis', { ns: 'documents-to-review-management' })
    }

    if (batchStatus === 'ready-for-disperse') {
      return t('listItem.chipStatusTexts.readyForDisperse', { ns: 'documents-completed-management' })
    }

    if (batchStatus === 'dispersing' || batchStatus === 'register-disperse-completed') {
      return t('listItem.chipStatusTexts.dispersing', { ns: 'documents-completed-management' })
    }

    if (batchStatus === 'completed') {
      return t('listItem.chipStatusTexts.completed', { ns: 'documents-completed-management' })
    }

    return t('listItem.chipStatusTexts.inProcess', { ns: 'documents-to-review-management' })
  }

  const handleCloseSession = () => {
    removeAuth?.()
    navigate('/', { replace: true, state: { sessionExpired: true } })
  }

  return (
    <>
      <div className="section-container">
        <Breadcrumbs aria-label="breadcrumb">
          <Typography
            key="3"
            className="breadcrumb-active"
          >
            {t('paymentMovements.title')}
          </Typography>
        </Breadcrumbs>
        <div
          style={{ marginTop: '24px' }}
          className="customer-company-fundings-history-container"
        >
          <div className="actions-bar">
            <div className="left-content">
              <div className="title">
                {batchesData.length === 0
                  ? t('paymentMovements.actionsBar.thereAreNoPaymentMovementsToShow')
                  : batchToSearch === ''
                    ? t('paymentMovements.actionsBar.batchesCount', { batchesCount: batchesData.length })
                    : t('paymentMovements.actionsBar.batchesCount', { batchesCount: searchedBatches.length })}
              </div>
              {componentDidMount.current === true && batchesData.length > 0 && (
                <div
                  style={{ cursor: 'pointer' }}
                  className="filter-button-container"
                  onClick={() => setOpenPaymentsHistoryModal(true)}
                >
                  <img
                    src={DownloadIcon}
                    alt="download-icon"
                  />
                  <div className="filter-button-text">{t('actionsBar.downloadReport', { ns: 'customer-details-management' })}</div>
                </div>
              )}
            </div>
            <TextField
              variant="standard"
              sx={textFieldWhiteBg}
              className="search-input"
              value={batchToSearch}
              error={!!errors.batchToSearch}
              helperText={errors.batchToSearch}
              label={t('paymentMovements.searchField.placeholder')}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault()
                  handleSetBatchesDataToSearch(batchToSearch, true)
                }
              }}
              onChange={(event) => handleSetBatchesDataToSearch(event.target.value, true)}
              onBlur={() => {
                if (batchToSearch === '') setErrors({})
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      edge="end"
                      aria-label="toggle password visibility"
                      sx={{ color: '#107569', right: '10px', bottom: '3px' }}
                    >
                      <img
                        src={SearchIcon}
                        alt="search-icon"
                      />
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
          </div>
        </div>
        {batchesData.length > 0 && (
          <div className="table-header-container table-header-batches">
            <div
              id="last-batches-header-date"
              className="text-header table-header-width-15"
            >
              {t('lastBatches.tableHeaders.date', { ns: 'reports-management' }).toUpperCase()}
            </div>
            <div
              id="last-batches-header-name"
              className="text-header table-header-width-15"
            >
              {t('lastBatches.tableHeaders.nameOfOperation', { ns: 'reports-management' }).toUpperCase()}
            </div>
            <div
              id="last-batches-header-id"
              className="text-header table-header-width-15"
            >
              {t('lastBatches.tableHeaders.batchId', { ns: 'reports-management' }).toUpperCase()}
            </div>
            <div
              id="last-batches-header-partner"
              className="text-header table-header-width-15"
            >
              {t('lastBatches.tableHeaders.bankingPartner', { ns: 'reports-management' }).toUpperCase()}
            </div>
            <div
              id="last-batches-header-num-payments"
              className="text-header table-header-width-15"
            >
              {t('lastBatches.tableHeaders.numberOfPayments', { ns: 'reports-management' }).toUpperCase()}
            </div>
            <div
              id="last-batches-header-amount"
              className="text-header table-header-width-15"
            >
              {t('lastBatches.tableHeaders.amount', { ns: 'reports-management' }).toUpperCase()}
            </div>
            <div
              id="last-batches-header-status"
              className="text-header table-header-width-15"
            >
              {t('lastBatches.tableHeaders.status', { ns: 'reports-management' }).toUpperCase()}
            </div>
          </div>
        )}
        {componentDidMount.current === false && (
          <div className="users-management-users-list-container">
            <div className="users-management-user-data-container-loader"></div>
            <div className="users-management-user-data-container-loader"></div>
            <div className="users-management-user-data-container-loader"></div>
            <div className="users-management-user-data-container-loader"></div>
            <div className="users-management-user-data-container-loader"></div>
          </div>
        )}
        {componentDidMount.current === true && batchesDataRef.current.length > 0 && (
          <>
            <div className="documents-list-container">
              {batchesList.map((batchData, index) => {
                const batchStatusText = handleGetBatchChipStatusText(batchData.status)

                return (
                  <div
                    key={index}
                    style={{ cursor: 'unset' }}
                    className="customer-details-list-item-data-container"
                  >
                    <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
                      <div className="customer-details-list-item-icon">
                        <img
                          src={CalendarIcon}
                          alt="calendar-icon"
                        />
                      </div>
                      <div className="customer-details-list-item-text">{format(new Date(batchData.createdAt), 'dd/MM/yyyy | HH:mm:ss')}</div>
                    </div>
                    <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
                      <div className="customer-details-list-item-text">{batchData.originalName.replace(/\.[^.]*$/, '').toUpperCase()}</div>
                    </div>
                    <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
                      <div className="customer-details-list-item-icon">
                        <img
                          src={TicketIcon}
                          alt="ticket-icon"
                        />
                      </div>
                      <div className="customer-details-list-item-text">{String(batchData._id).slice(-8)}</div>
                    </div>
                    <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
                      <div className="customer-details-list-item-icon">
                        <img
                          src={CheckIconGreen}
                          alt="check-icon-green"
                        />
                      </div>
                      <div className="customer-details-list-item-text">
                        {batchData.cobrePaymentReference && Array.isArray(batchData.cobrePaymentReference) && batchData.cobrePaymentReference.length > 0 ? 'COBRE' : 'MONNET'}
                      </div>
                    </div>
                    <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
                      <div className="customer-details-list-item-icon">
                        <img
                          src={HashIcon}
                          alt="hash-icon"
                        />
                      </div>
                      <div className="customer-details-list-item-text">{batchData.paymentCount}</div>
                    </div>
                    <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
                      <div className="customer-details-list-item-icon">
                        <img
                          src={MoneyGreenIcon}
                          alt="money-icon"
                        />
                      </div>
                      <div className="customer-details-list-item-text">{`${numberWithCommas(batchData.totalAmount)} COP`}</div>
                    </div>
                    <div className="customer-company-table-column last-batches-table-width-15 max-width-unset">
                      <Chip
                        status={batchData.status}
                        label={batchStatusText}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
            <Pagination
              pageSize={pageSize}
              currentPage={currentListPage}
              handlePaginationPageChange={(page: number) => {
                setTimeout(() => {
                  setScrollToTop(true)
                  setCurrentListPage(page)
                  currentListPageRef.current = page
                }, 100)
              }}
              handleChangePageSize={(pageSizeToSet: string) => {
                setScrollToTop(true)
                setPageSize(Number(pageSizeToSet))
              }}
              totalCount={batchesData.length}
            />
          </>
        )}
      </div>
      {openPaymentsHistoryModal && (
        <PaymentsHistoryModal
          handleSetModalOpenState={(openState: boolean) => {
            setOpenPaymentsHistoryModal(openState)
          }}
          filtersFromOutside={{
            belongsTo: '',
            reportType: 'batches'
          }}
        />
      )}
    </>
  )
}

export default PaymentMovementsManagement
